import { DocumentRenderData, RequiredField } from '../types'
import {
  MergeField,
  CollapsibleClause,
  SignatureBlock,
  QISignatureBlock,
  blank,
  formatDate,
} from './shared'

export const exchangeAgreementRequiredFields: RequiredField[] = [
  { key: 'exchanger.names', description: 'Exchanger full name', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.street', description: 'Exchanger street address', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.city', description: 'Exchanger city', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.state', description: 'Exchanger state', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.zipCode', description: 'Exchanger ZIP code', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'exchanger.taxIdNumber', description: 'Exchanger Tax ID / SSN', sourceHub: 'sales', sourceStep: 'Exchanger Info' },
  { key: 'property.addressOrLegalDescription', description: 'Relinquished property address', sourceHub: 'sales', sourceStep: 'Relinquished Property' },
]

interface Props {
  data: DocumentRenderData
}

function maskedTaxId(taxId: string): string {
  if (!taxId) return ''
  const last4 = taxId.slice(-4)
  return `***-**-${last4}`
}

function effectiveDate(signature: DocumentRenderData['signature']): string {
  if (signature?.capturedAt) return new Date(signature.capturedAt).toLocaleDateString()
  return new Date().toLocaleDateString()
}

export function ExchangeAgreementTemplate({ data }: Props) {
  const { exchanger, property, buyer, signature, bundleMeta, escrow, counterSignatures } = data
  const exchangerLegal = exchanger.legalEntityDescription || exchanger.names
  const exchangerAddress =
    exchanger.street && exchanger.city && exchanger.state && exchanger.zipCode
      ? `${exchanger.street}, ${exchanger.city}, ${exchanger.state} ${exchanger.zipCode}`
      : exchanger.homeAddress

  const agreementDateDisplay = effectiveDate(signature)

  const wireBankName = escrow?.bankName ?? 'Western Alliance Bank'
  const wireBankAddress = escrow?.bankAddress ?? 'One E Washington St, Phoenix AZ 85004'
  const wireAba = escrow?.abaRouting ?? '___________'
  const wireAccount = escrow?.accountNumber ?? '___________'
  const wireBeneficiary = escrow?.beneficiaryName ?? `Open 1031, Inc. FBO ${exchangerLegal}`
  const wireBeneficiaryAddress = escrow?.beneficiaryAddress ?? '1 N Central Ave, Phoenix AZ 85004'

  const qiOfficerName = counterSignatures?.qiOfficerName ?? bundleMeta?.intermediaryOfficerName ?? 'Tom Bottenberg'

  const purchaserSignerName = counterSignatures?.purchaserSignerName ?? 'James R. Whitfield'
  const purchaserSignerTitle = counterSignatures?.purchaserSignerTitle ?? 'Managing Member'
  const purchaserDaysOffset = counterSignatures?.purchaserSignerDaysBeforeExchanger ?? 3

  const purchaserSignDate = (() => {
    if (signature?.capturedAt) {
      const base = new Date(signature.capturedAt)
      base.setDate(base.getDate() - purchaserDaysOffset)
      return base.toLocaleDateString()
    }
    const base = new Date()
    base.setDate(base.getDate() - purchaserDaysOffset)
    return base.toLocaleDateString()
  })()

  return (
    <div className="space-y-6 text-sm text-foreground/85 font-serif">
      <div className="text-center">
        <h4 className="text-xl font-bold tracking-wide text-foreground">
          PHASE ONE — SALE BUNDLE
        </h4>
        <p className="text-xs text-muted-foreground/80 mt-1 italic">
          Open 1031, Inc. — Qualified Intermediary Services under IRC §1031
        </p>
      </div>

      <div className="rounded-md border border-border bg-card/50 p-3 space-y-1 text-xs">
        <MergeField label="OPEN1031 FILE NO.:" value={bundleMeta?.open1031FileNumber} width={28} />
        <MergeField label="EXCHANGE AGREEMENT DATE:" value={agreementDateDisplay} width={20} />
        <MergeField label="EXCHANGE OFFICER:" value={bundleMeta?.exchangeOfficer ?? qiOfficerName} width={28} />
        <MergeField label="SETTLEMENT AGENT:" value={bundleMeta?.settlementAgent} width={28} />
        <MergeField label="ESCROW FILE NO.:" value={bundleMeta?.escrowFileNumber ?? escrow?.fileNumber} width={28} />
      </div>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">1. INTRODUCTION</h5>
        <p className="text-xs leading-relaxed">
          Dear <span className="font-mono">{blank(exchangerLegal, 30)}</span>,
        </p>
        <p className="text-xs leading-relaxed">
          Thank you for choosing Open 1031, Inc. to serve as your qualified intermediary for your
          like-kind exchange transaction under Section 1031 of the Internal Revenue Code (your &ldquo;1031
          Exchange&rdquo;). This bundle contains the documents required to commence your 1031 Exchange,
          including the Exchange Agreement, Relinquished Property Assignment, Closing Instructions,
          Notice to Buyer, and Wiring Instructions.
        </p>
        <p className="text-xs leading-relaxed">
          We are not professional advisors and do not provide legal, tax, investment, or accounting
          advice. You should consult with your independent legal, tax, and financial advisors prior
          to signing.
        </p>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">2. EXCHANGE AGREEMENT</h5>
        <p className="text-xs leading-relaxed">
          THIS EXCHANGE AGREEMENT (the &ldquo;Agreement&rdquo;) is entered into and effective as of{' '}
          <span className="font-mono">{blank(agreementDateDisplay, 20)}</span>{' '}
          by and between <strong>Open 1031, Inc.</strong>, a Delaware corporation, as Qualified
          Intermediary (&ldquo;Intermediary&rdquo;), under Exchange file no.{' '}
          <span className="font-mono">{blank(bundleMeta?.open1031FileNumber, 18)}</span>, and{' '}
          <strong>{blank(exchangerLegal, 35)}</strong> (&ldquo;Exchanger&rdquo;).
        </p>

        <div className="border-l-4 border-primary-500 pl-4 bg-card p-3 rounded">
          <h6 className="font-semibold mb-2 text-xs">EXCHANGER INFORMATION</h6>
          <div className="space-y-1 text-xs">
            <p><span className="font-medium">Name:</span> {blank(exchangerLegal, 40)}</p>
            <p><span className="font-medium">Address:</span> {blank(exchangerAddress, 50)}</p>
            <p>
              <span className="font-medium">Tax ID / SSN:</span>{' '}
              {data.showSSN ? exchanger.taxIdNumber : maskedTaxId(exchanger.taxIdNumber)}
            </p>
          </div>
        </div>

        {property && (
          <div className="border-l-4 border-blue-500 pl-4 bg-card p-3 rounded">
            <h6 className="font-semibold mb-2 text-xs">RELINQUISHED PROPERTY</h6>
            <p className="text-xs break-words whitespace-normal">
              {blank(property.addressOrLegalDescription, 60)}
            </p>
            {property.saleDate && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Anticipated Sale Date: {formatDate(property.saleDate)}
              </p>
            )}
          </div>
        )}

        {buyer && (
          <div className="border-l-4 border-green-500 pl-4 bg-card p-3 rounded">
            <h6 className="font-semibold mb-2 text-xs">BUYER OF RELINQUISHED PROPERTY</h6>
            <p className="text-xs">{blank(buyer.names, 40)}</p>
          </div>
        )}

        <div className="space-y-1 text-xs pt-2">
          <p className="font-semibold">KEY EXCHANGE AGREEMENT TERMS</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              Intermediary agrees to facilitate the tax-deferred exchange pursuant to Section 1031 of
              the Internal Revenue Code and Treas. Reg. §1.1031(k)-1(g)(4).
            </li>
            <li>
              Exchanger assigns all rights to the Relinquished Property sale proceeds to Intermediary
              for the duration of the exchange period.
            </li>
            <li>
              Replacement properties must be identified in writing within{' '}
              <strong>45 days</strong> of the Relinquished Property closing.
            </li>
            <li>
              Exchange must be completed (replacement property(ies) acquired) within{' '}
              <strong>180 days</strong> of the Relinquished Property closing.
            </li>
            <li>
              All exchange funds will be held in a segregated qualified escrow account at{' '}
              {wireBankName} to prevent constructive receipt by Exchanger.
            </li>
            <li>
              Intermediary fee for Phase One (Relinquished) plus the first Replacement Property
              acquisition: <strong>$895</strong>.
            </li>
          </ul>
        </div>

        <CollapsibleClause title="View full Exchange Agreement clauses">
          <p>
            <strong>Article 1 — Engagement.</strong> Exchanger engages Intermediary, and Intermediary
            accepts engagement, solely to act as &ldquo;qualified intermediary&rdquo; within the
            meaning of Treasury Regulation §1.1031(k)-1(g)(4).
          </p>
          <p>
            <strong>Article 2 — Exchange Period.</strong> The 45-day Identification Period and 180-day
            Exchange Period run from the date of conveyance of the Relinquished Property.
          </p>
          <p>
            <strong>Article 3 — Identification.</strong> Exchanger may identify Eligible Replacement
            Properties under the Three Property Rule, the 200% Rule, or the 95% Rule.
          </p>
          <p>
            <strong>Article 4 — Exchange Funds.</strong> All Exchange Proceeds are held in a
            segregated FDIC-insured qualified escrow account at {wireBankName}. Exchanger has
            no right to receive, pledge, borrow or otherwise obtain the benefits of those funds prior
            to the events specified in Treas. Reg. §1.1031(k)-1(g)(6).
          </p>
          <p>
            <strong>Article 5 — Fees.</strong> Exchanger agrees to compensate Intermediary the
            $895 fee for the Phase One (Relinquished) portion of the exchange and the first
            Replacement Property. Additional Replacement Properties may incur additional fees per
            the published Open 1031 fee schedule.
          </p>
          <p>
            <strong>Article 6 — Indemnification.</strong> Exchanger shall indemnify, hold harmless,
            and defend Intermediary against any claims arising out of the transactions contemplated
            by this Agreement, except to the extent attributable to Intermediary&rsquo;s gross negligence
            or willful misconduct.
          </p>
          <p>
            <strong>Article 7 — Representations &amp; Warranties.</strong> The full text of party
            representations and warranties is preserved in the .docx source linked at the top of this
            modal.
          </p>
          <p>
            <strong>Article 8 — General Provisions.</strong> Including governing law (California),
            waiver of jury trial, no third-party beneficiaries, integration, and execution in
            counterparts. The full text is preserved in the .docx source linked at the top of this
            modal.
          </p>
        </CollapsibleClause>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">3. RELINQUISHED PROPERTY ASSIGNMENT</h5>
        <p className="text-xs leading-relaxed">
          Pursuant to the Exchange Agreement, Exchanger hereby assigns to Intermediary all of
          Exchanger&rsquo;s rights (but not its duties and obligations) under the purchase and sale
          agreement for the Relinquished Property, including the right to receive the Exchange
          Proceeds from the Buyer. The Buyer is hereby directed to convey title to the Relinquished
          Property directly to the Buyer and to wire the net Exchange Proceeds to the qualified
          escrow account identified in Section 5 below.
        </p>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">4. CLOSING INSTRUCTIONS (PHASE ONE)</h5>
        <div className="rounded-md border border-border bg-card/50 p-3 space-y-1 text-xs">
          <MergeField label="Date:" value={agreementDateDisplay} width={20} />
          <MergeField label="Settlement Agent:" value={bundleMeta?.settlementAgent} width={32} />
          <MergeField label="Escrow File No.:" value={bundleMeta?.escrowFileNumber ?? escrow?.fileNumber} width={28} />
          <MergeField label="Open 1031 File:" value={bundleMeta?.open1031FileNumber} width={28} />
          <MergeField label="Exchange Officer:" value={bundleMeta?.exchangeOfficer ?? qiOfficerName} width={32} />
          <MergeField label="Exchanger:" value={exchangerLegal} width={50} />
          <MergeField label="Property Address:" value={property?.addressOrLegalDescription} width={50} />
        </div>
        <ol className="list-decimal list-inside space-y-1 text-xs">
          <li>
            Settlement Agent will receive the executed Phase One Agreement and Assignment (Exchange
            Agreement) and Wiring Instructions with this bundle.
          </li>
          <li>
            <strong>Exchange Fee:</strong> Open 1031 fees will be deducted from the Exchange Proceeds
            wire by the Qualified Intermediary. Do not charge an Open 1031 fee on the Settlement
            Statement.
          </li>
          <li>
            The Settlement Statement should list the Seller as &ldquo;Open 1031, Inc. as Qualified
            Intermediary for {blank(exchangerLegal, 30)}.&rdquo;
          </li>
          <li>
            Please email the executed original documents to the Qualified Intermediary.
          </li>
        </ol>
        <p className="text-[11px] italic text-muted-foreground">
          IMPORTANT REMINDER: Exchange funds should be applied solely to acquire real property and not
          to acquire personal property or to fund loan reserves.
        </p>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">5. WIRE INSTRUCTIONS (THE QI BANK)</h5>
        <p className="text-xs leading-relaxed">
          Open 1031, Inc. is acting as the Qualified Intermediary for the referenced Exchanger.
          Settlement Agent should wire only the net proceeds of sale (the Exchange Funds) to the
          following account:
        </p>
        <div className="rounded-md border border-border bg-card/50 p-3 space-y-0.5 text-xs font-mono">
          <p>Bank: {wireBankName}</p>
          <p>{wireBankAddress}</p>
          <p>ABA Routing Number: {wireAba}</p>
          <p>Beneficiary Account Number: {wireAccount}</p>
          <p>Beneficiary Address: {wireBeneficiaryAddress}</p>
          <p>
            Beneficiary Name: {wireBeneficiary}
          </p>
        </div>
        <p className="text-[11px] italic text-muted-foreground">
          NOTE: Do not deposit a check into this account; we do not accept ACH credits.
        </p>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">6. NOTICE TO BUYER OF RELINQUISHED PROPERTY</h5>
        <p className="text-xs leading-relaxed">
          Purchaser acknowledges that Seller may engage in a tax-deferred exchange (the &ldquo;Exchange&rdquo;)
          pursuant to Section 1031 of the Internal Revenue Code with respect to the disposition of the
          Property. As an accommodation to Seller, Purchaser agrees to cooperate with Existing Owner
          in connection with the Exchange and hereby consents to the assignment of the purchase and
          sale agreement to the qualified intermediary, subject to the conditions set forth in the
          full Notice to Buyer (see source .docx).
        </p>
        {buyer && (
          <div className="rounded-md border border-border bg-card/50 p-3 space-y-2 text-xs">
            <p className="font-semibold">Purchaser Acknowledgment:</p>
            <p>{blank(buyer.names, 50)}</p>
            <div className="pt-1">
              <p className="font-mono">
                By:{' '}
                <span
                  className="text-base text-primary-900 dark:text-primary-100"
                  style={{ fontFamily: 'cursive' }}
                >
                  {purchaserSignerName}
                </span>
              </p>
              <p className="text-muted-foreground text-[11px]">
                {purchaserSignerName}, {purchaserSignerTitle}
              </p>
              <p className="font-mono text-[11px]">Date: {purchaserSignDate}</p>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">7. WHEN CAN I GET MY MONEY BACK?</h5>
        <p className="text-xs leading-relaxed">
          Treas. Reg. §1.1031(k)-1(g)(6) requires that the agreement with the qualified intermediary
          expressly limit the taxpayer&rsquo;s rights to receive, pledge, borrow, or otherwise obtain
          the benefits of money or other property before the end of the exchange period. In plain
          English, Open 1031 cannot legally release funds back to the Exchanger until:
        </p>
        <ul className="list-disc list-inside space-y-1 text-xs ml-2">
          <li>The 45-day identification period has expired with no properties identified, OR</li>
          <li>The 45-day identification period has expired and all identified property(ies) have been closed on by Exchanger, OR</li>
          <li>The 180-day exchange period has expired.</li>
        </ul>
      </section>

      <section className="border-t-2 border-border pt-4 space-y-4">
        <SignatureBlock
          label="EXCHANGER SIGNATURE:"
          signerName={exchangerLegal}
          signature={signature}
        />
        {signature?.captured ? (
          <QISignatureBlock
            signature={signature}
            signatoryName={qiOfficerName}
          />
        ) : (
          <div className="space-y-1 text-xs text-muted-foreground italic">
            <span>Pending exchange officer countersignature</span>
          </div>
        )}
      </section>
    </div>
  )
}
